import { MouseEvent } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Stack, Typography } from "@mui/material"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedEnum } from "../../../../localization/components/AppLocalized"
import { MessageAdditionalDataChannelsType } from "../messagesTypes"

const useStyles = makeStyles()((theme) => ({
    card: {
        flex: 1,
        borderRadius: "1.5rem",
        gap: ".75rem",
        padding: "1.25rem 1rem",
        width: "10rem",
        textAlign: "center",
        cursor: "pointer",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        transition: "background-color 150ms ease, transform 150ms ease, color 150ms ease",
        alignItems: "center",
        // minHeight: "10rem",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[100],
            transform: "translateY(-1px)",
        },
    },
    cardSelected: {
        backgroundColor: theme.palette.tailwind.slate[700],
        color: theme.palette.common.white,
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[800],
        },
    },
    cardDisabled: {
        opacity: 0.4,
        cursor: "default",
        pointerEvents: "none",
    },
    iconWrapper: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.common.black,
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    description: {
        padding: "0 .875rem",
    },
    descriptionSelected: {
        color: theme.palette.tailwind.slate[200],
    },
}))

export type ChannelCardConfig = Readonly<{
    type: MessageAdditionalDataChannelsType
    icon: IconProp
    disabled?: boolean
}>

type MessageChannelCardProps = Readonly<{
    option: ChannelCardConfig
    selected: boolean
    onToggle: (channel: MessageAdditionalDataChannelsType) => void
}>

export function MessageChannelCard({ option, selected, onToggle }: MessageChannelCardProps) {
    const { classes, cx } = useStyles()

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (option.disabled) return
        onToggle(option.type)
    }

    return (
        <Stack
            className={cx(classes.card, {
                [classes.cardSelected]: selected,
                [classes.cardDisabled]: option.disabled,
            })}
            onClick={handleClick}
        >
            <Box className={classes.iconWrapper}>
                <FontAwesomeIcon icon={option.icon} size="lg" />
            </Box>
            <Stack gap=".5rem">
                <LocalizedEnum base="create-or-edit-message-field-channel-type-mode" enumValue={option.type}>
                    <Typography variant="body1">{option.type}</Typography>
                </LocalizedEnum>
                <LocalizedEnum
                    base="create-or-edit-message-field-channel-type-description-mode"
                    enumValue={option.type}
                >
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        className={cx(classes.description, {
                            [classes.descriptionSelected]: selected,
                        })}
                    >
                        {option.type}
                    </Typography>
                </LocalizedEnum>
            </Stack>
        </Stack>
    )
}
